/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useCallback, useState } from "react";
import { Card } from "@material-ui/core";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";
import { AgentExtensionsTable } from "./AgentExtensionsTable";
import { GlobalSettings } from "./GlobalSettings";
import { SyncAgents } from "./SyncAgents";
import { AgentExtensionEdit } from "./AgentExtensionEdit";
import AgentExtensionsActionsHeader from "./AgentExtensionsActionHeader";

const COMPONENT_NAME = "Admin settingsManager/AgentExtensions/AgentExtensions";
/**
 * Component used to manage agents
 * @param {Object} agents list of agents from dynamo table.
 * @param {Boolean} isLoading load the agent when set to true
 * @param {Object} globalSettings contains the global configurations.
 * @param {Function} saveGlobalSetting function use to save the global settings
 * @param {Function} syncAgentsList sync the agents from amazon connect
 * @param {Function} handleSave function use to save agent detail
 * @param {Boolean} isAgentLoading load the agent detail when set to true.
 * @param {Function} fetchAgentById function use to fetch connect user by ID
 */
export const AgentExtensions = ({
	agents,
	setIsLoading,
	globalSettings,
	saveGlobalSetting,
	syncAgentsList,
	handleSave,
	isAgentLoading,
	fetchAgentById,
}) => {
	const [showGlobalSettingsModal, setShowGlobalSettingsModal] = useState(false);
	const [showSyncAgentModal, setShowSyncAgentModal] = useState(false);
	const [editAgentModal, setEditAgentModal] = useState(false);
	const [agent, setAgent] = useState({});
	const [connect, setConnect] = useState({});
	const [searchTerm, setSearchTerm] = useState("");

	const editAgent = useCallback(
		async (agent) => {
			try {
				setIsLoading(true);
				setAgent(agent);
				setEditAgentModal(true);
				const connectUser = await fetchAgentById(agent.agentId);
				setConnect(connectUser);
			} catch (error) {
				v2.error(`Error while fetching agent by agentId.`, {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.GET_AGENT_BY_ID_ERROR,
				});
			} finally {
				setIsLoading(false);
			}
		},
		[fetchAgentById, setIsLoading],
	);

	return (
		<Card>
			<AgentExtensionsActionsHeader
				filteredTerm={setSearchTerm}
				setShowGlobalSettingsModal={setShowGlobalSettingsModal}
				setShowSyncAgentModal={setShowSyncAgentModal}
			/>
			{agents && (
				<div>
					<AgentExtensionsTable
						editAgent={editAgent}
						agents={agents}
						searchTerm={searchTerm}
						globalSettings={globalSettings}
					/>

					{globalSettings && (
						<GlobalSettings
							globalSettings={globalSettings}
							showGlobalSettingsModal={showGlobalSettingsModal}
							setShowGlobalSettingsModal={setShowGlobalSettingsModal}
							saveGlobalSettingsHandler={saveGlobalSetting}
						/>
					)}

					{agent && editAgentModal && (
						<AgentExtensionEdit
							agent={agent}
							editAgentModal={editAgentModal}
							setEditAgentModal={setEditAgentModal}
							saveAgentHandler={handleSave}
							isAgentLoading={isAgentLoading}
							connect={connect}
							setAgent={setAgent}
						/>
					)}

					<SyncAgents
						setShowSyncAgentModal={setShowSyncAgentModal}
						showSyncAgentModal={showSyncAgentModal}
						syncAgentsHandler={syncAgentsList}
					/>
				</div>
			)}
		</Card>
	);
};

export default AgentExtensions;
